import { useState } from 'react';

import { usePlans, type PlanStatus } from '../../hooks/use-plans';

const STATUS_COLORS: Record<PlanStatus, string> = {
  draft: 'var(--text-mute)',
  pending_approval: 'var(--accent)',
  approved: '#1f6feb',
  executing: '#1f6feb',
  completed: '#2f855a',
  revised: 'var(--accent)',
  denied: '#c0392b',
};

const STATUS_LABELS: Record<PlanStatus, string> = {
  draft: 'Draft',
  pending_approval: 'Awaiting approval',
  approved: 'Approved',
  executing: 'Executing',
  completed: 'Completed',
  revised: 'Revised',
  denied: 'Denied',
};

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const now = Date.now();
  const diff = now - d.getTime();
  const min = Math.floor(diff / 60_000);
  if (min < 1) return 'now';
  if (min < 60) return `${min}m ago`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export function PlansView() {
  const { plans, status, error, filters, setFilters, counts, approvePlan, denyPlan } = usePlans();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [denyingId, setDenyingId] = useState<string | null>(null);
  const [denyReason, setDenyReason] = useState('');

  const activeCount = (counts.pending_approval ?? 0) + (counts.executing ?? 0);

  const handleApprove = async (id: string) => {
    setPendingAction(id);
    setActionError(null);
    try {
      await approvePlan(id);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to approve');
    } finally {
      setPendingAction(null);
    }
  };

  const handleDeny = async (id: string) => {
    setPendingAction(id);
    setActionError(null);
    try {
      await denyPlan(id, denyReason.trim() || undefined);
      setDenyingId(null);
      setDenyReason('');
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to deny');
    } finally {
      setPendingAction(null);
    }
  };

  return (
    <div style={{ padding: 20, height: '100%', overflowY: 'auto', background: 'var(--bg)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>Plans</div>
        <div style={{ fontSize: 11, color: 'var(--text-soft)' }}>
          {plans.length} shown · {activeCount} active
        </div>
        <input
          placeholder="Filter by plan, task, or conversation id…"
          value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          style={{
            marginLeft: 'auto',
            padding: '5px 10px',
            fontSize: 12,
            border: '1px solid var(--border-strong)',
            borderRadius: 6,
            background: 'var(--surface)',
            color: 'var(--text)',
            outline: 'none',
            fontFamily: 'inherit',
            minWidth: 280,
          }}
        />
        <select
          value={filters.statuses.length === 7 ? 'all' : filters.statuses.join(',') || 'all'}
          onChange={(e) => {
            const v = e.target.value;
            if (v === 'all') {
              setFilters({
                ...filters,
                statuses: ['draft', 'pending_approval', 'approved', 'executing', 'completed', 'revised', 'denied'],
              });
            } else if (v === 'active') {
              setFilters({ ...filters, statuses: ['pending_approval', 'approved', 'executing'] });
            } else if (v === 'done') {
              setFilters({ ...filters, statuses: ['completed'] });
            } else if (v === 'denied') {
              setFilters({ ...filters, statuses: ['denied'] });
            } else if (v === 'awaiting') {
              setFilters({ ...filters, statuses: ['pending_approval'] });
            }
          }}
          style={{
            padding: '5px 10px',
            fontSize: 12,
            border: '1px solid var(--border-strong)',
            borderRadius: 6,
            background: 'var(--surface)',
            color: 'var(--text)',
            outline: 'none',
            fontFamily: 'inherit',
            cursor: 'pointer',
          }}
        >
          <option value="active">Active (awaiting · approved · executing)</option>
          <option value="awaiting">Awaiting approval only</option>
          <option value="done">Completed</option>
          <option value="denied">Denied</option>
          <option value="all">All plans</option>
        </select>
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-soft)', marginBottom: 16 }}>
        Agent-proposed execution plans awaiting your review or already running.
      </div>

      {actionError && (
        <div
          role="alert"
          style={{
            marginBottom: 12,
            padding: '8px 12px',
            background: 'rgba(192,57,43,0.06)',
            border: '1px solid rgba(192,57,43,0.18)',
            borderRadius: 6,
            fontSize: 12,
            color: 'var(--kind-failure)',
          }}
        >
          {actionError}
        </div>
      )}

      {status === 'loading' && (
        <div style={{ padding: 16, fontSize: 12, color: 'var(--text-mute)' }}>Loading…</div>
      )}
      {status === 'error' && (
        <div style={{ padding: 16, fontSize: 12, color: 'var(--kind-failure)' }}>
          {error ?? 'Failed to load plans.'}
        </div>
      )}
      {status === 'success' && plans.length === 0 && (
        <div style={{ padding: 40, fontSize: 12, color: 'var(--text-mute)', textAlign: 'center' }}>
          No plans match those filters.
        </div>
      )}

      {plans.map((p) => {
        const expanded = expandedId === p.id;
        const awaiting = p.status === 'pending_approval';
        const denying = denyingId === p.id;
        const busy = pendingAction === p.id;
        return (
          <div
            key={p.id}
            style={{
              background: 'var(--surface)',
              border: `1px solid ${awaiting ? 'var(--accent-soft)' : 'var(--border)'}`,
              borderRadius: 10,
              padding: 16,
              marginBottom: 12,
            }}
          >
            <div
              onClick={() => setExpandedId(expanded ? null : p.id)}
              style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}
            >
              <span style={{ fontSize: 18 }}>📋</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: 'var(--text)',
                    fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                  }}
                >
                  {p.id}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-soft)' }}>
                  v{p.version} · created {fmtDate(p.created_at)}
                  {p.conversation_id && ` · conv ${p.conversation_id.slice(0, 12)}…`}
                  {p.task_id && ` · task ${p.task_id.slice(0, 12)}…`}
                </div>
              </div>
              <span
                style={{
                  fontSize: 11,
                  color: STATUS_COLORS[p.status],
                  padding: '3px 10px',
                  background: 'var(--bg-alt)',
                  border: `1px solid ${STATUS_COLORS[p.status]}30`,
                  borderRadius: 999,
                  fontWeight: 500,
                  textTransform: 'capitalize',
                  whiteSpace: 'nowrap',
                }}
              >
                {STATUS_LABELS[p.status]}
              </span>
              <span
                style={{
                  color: 'var(--text-mute)',
                  fontSize: 11,
                  transition: 'transform 120ms ease',
                  transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
                  marginLeft: 4,
                }}
              >
                ›
              </span>
            </div>

            {expanded && (
              <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
                {/* Steps */}
                {p.steps.length > 0 && (
                  <div style={{ marginBottom: 12 }}>
                    <SectionLabel>Steps ({p.steps.length})</SectionLabel>
                    <div
                      style={{
                        background: 'var(--bg-alt2)',
                        border: '1px solid var(--border)',
                        borderRadius: 6,
                        padding: 10,
                        fontSize: 11,
                        fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                        color: 'var(--text)',
                        maxHeight: 280,
                        overflow: 'auto',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}
                    >
                      {JSON.stringify(p.steps, null, 2)}
                    </div>
                  </div>
                )}

                {p.data_access.length > 0 && (
                  <div style={{ marginBottom: 12 }}>
                    <SectionLabel>Data access requested ({p.data_access.length})</SectionLabel>
                    <div
                      style={{
                        background: 'var(--bg-alt2)',
                        border: '1px solid var(--border)',
                        borderRadius: 6,
                        padding: 10,
                        fontSize: 11,
                        fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                        color: 'var(--text)',
                        maxHeight: 200,
                        overflow: 'auto',
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {JSON.stringify(p.data_access, null, 2)}
                    </div>
                  </div>
                )}

                {p.denial_reason && (
                  <div style={{ marginBottom: 12 }}>
                    <SectionLabel>Denial reason</SectionLabel>
                    <div
                      style={{
                        padding: '8px 10px',
                        background: 'rgba(192,57,43,0.06)',
                        border: '1px solid rgba(192,57,43,0.18)',
                        borderRadius: 6,
                        fontSize: 12,
                        color: 'var(--kind-failure)',
                      }}
                    >
                      {p.denial_reason}
                    </div>
                  </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  {p.approved_at && (
                    <Meta label="Approved" value={fmtDate(p.approved_at)} />
                  )}
                  {p.approved_by && <Meta label="Approved by" value={p.approved_by} mono />}
                  <Meta label="Updated" value={fmtDate(p.updated_at)} />
                  <Meta label="Tenant" value={p.tenant_id} mono />
                </div>
              </div>
            )}

            {awaiting && (
              <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
                {denying ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <input
                      value={denyReason}
                      onChange={(e) => setDenyReason(e.target.value)}
                      placeholder="Reason for denial (optional)"
                      autoFocus
                      style={{
                        padding: '7px 10px',
                        fontSize: 12,
                        border: '1px solid var(--border-strong)',
                        borderRadius: 6,
                        background: 'var(--bg)',
                        color: 'var(--text)',
                        outline: 'none',
                        fontFamily: 'inherit',
                      }}
                    />
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                      <button
                        type="button"
                        onClick={() => {
                          setDenyingId(null);
                          setDenyReason('');
                        }}
                        disabled={busy}
                        style={secondaryButtonStyle}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeny(p.id)}
                        disabled={busy}
                        style={{
                          padding: '7px 14px',
                          fontSize: 12,
                          fontWeight: 500,
                          background: 'var(--kind-failure)',
                          color: '#fff',
                          border: 0,
                          borderRadius: 6,
                          cursor: busy ? 'not-allowed' : 'pointer',
                          fontFamily: 'inherit',
                        }}
                      >
                        {busy ? 'Denying…' : 'Confirm deny'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      type="button"
                      onClick={() => handleApprove(p.id)}
                      disabled={busy}
                      style={{
                        padding: '7px 14px',
                        fontSize: 12,
                        fontWeight: 500,
                        background: 'var(--accent)',
                        color: '#fff',
                        border: 0,
                        borderRadius: 6,
                        cursor: busy ? 'not-allowed' : 'pointer',
                        opacity: busy ? 0.6 : 1,
                        fontFamily: 'inherit',
                      }}
                    >
                      {busy ? 'Approving…' : 'Approve & run'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setDenyingId(p.id)}
                      disabled={busy}
                      style={{
                        padding: '7px 14px',
                        fontSize: 12,
                        fontWeight: 500,
                        background: 'transparent',
                        color: 'var(--text)',
                        border: '1px solid var(--border-strong)',
                        borderRadius: 6,
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                      }}
                    >
                      Deny
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: 10,
        color: 'var(--text-soft)',
        textTransform: 'uppercase',
        letterSpacing: 0.4,
        fontWeight: 600,
        marginBottom: 6,
      }}
    >
      {children}
    </div>
  );
}

function Meta({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <div
        style={{
          fontSize: 10,
          color: 'var(--text-mute)',
          textTransform: 'uppercase',
          letterSpacing: 0.4,
          fontWeight: 600,
          marginBottom: 2,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 12,
          color: 'var(--text)',
          fontFamily: mono ? 'ui-monospace, SFMono-Regular, monospace' : 'inherit',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {value}
      </div>
    </div>
  );
}

const secondaryButtonStyle = {
  padding: '7px 14px',
  fontSize: 12,
  fontWeight: 500,
  background: 'var(--surface)',
  color: 'var(--text)',
  border: '1px solid var(--border-strong)',
  borderRadius: 6,
  cursor: 'pointer',
  fontFamily: 'inherit',
} satisfies React.CSSProperties;
